import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

export interface MindmapNode {
  id: string;
  label: string;
  type: string;
  ring: number;
  importance?: number;
  color?: string; // Custom border color for the node
  children?: MindmapNode[];
  meta?: {
    section?: string;
    summary?: string;
    sources?: string[];
    citations?: Array<{
      label: string;
      url: string;
    }>;
  };
}

export interface MindmapLink {
  source: string;
  target: string;
  type: "hierarchy" | "reference";
}

export interface MindmapResponse {
  root: MindmapNode;
  nodes?: MindmapNode[];
  links?: MindmapLink[];
}

export interface GenerateMindmapPayload {
  regulation_name: string;
  org_name: string;
  maxPrimarySpokes?: number;
  includeDetails?: boolean;
  includeCrossLinks?: boolean;
  facility_context?: {
    description?: string;
    facilityType?: string;
    state?: string;
    cityOrCounty?: string;
    country?: string;
  };
}

const BASE_URL = getApiOrigin();
const MINDMAP_ENDPOINT = "/api/v1/mindmap/generate";

export async function generateMindmap(
  payload: GenerateMindmapPayload
): Promise<MindmapResponse> {
  try {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}${MINDMAP_ENDPOINT}`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    check401(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP ${response.status}: ${errorText || response.statusText}`
      );
    }

    const data = await response.json();
    return data as MindmapResponse;
  } catch (error) {
    console.error("Error generating mindmap:", error);
    throw new Error(
      `Failed to generate mindmap: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

// Color schemes for different node types (from original mindmap)
export const nodeColors: Record<string, string> = {
  regulation: "#3b82f6",
  topic: "#8b5cf6",
  subtopic: "#06b6d4",
  penalty: "#ef4444",
  facility: "#10b981",
  training: "#f59e0b",
  case: "#ec4899",
  procedure: "#6366f1",
  "tech-safeguard": "#84cc16",
};
